import { type ReactNode, useEffect, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@clerk/react";
import {
  ChevronRightIcon,
  PauseCircleIcon,
  PlayCircleIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Frame,
  FrameHeader,
  FrameTitle,
  FramePanel,
  FrameFooter,
  FrameAction,
} from "@/components/ui/frame";
import {
  SwitchCard,
  SwitchCardSwitch,
  SwitchCardContent,
  SwitchCardLabel,
} from "@/components/ui/switch-card";
import AmyBadge from "@/components/ui/amy-badge";
import { BadgeStatus } from "@/components/dashboard_campaigns/status-badges";
import { useCampaignStore } from "@/stores";
import { useEveActionsStore } from "@/stores/eve/eveActionsStore";
import { EvePipelinePhase } from "@/types/eve";
import { entryStatusFor } from "./shared";

/** One tier's row in the per-tier creator flow. */
export interface TierFlow {
  tier: number;
  /** The tier's offer ("55% Rev Share"). */
  offer: string;
  pipeline: EvePipelinePhase[];
  /** The tier's ⋯ menu. */
  menu?: ReactNode;
}

/** Pending Activation › <enabled phases> › Completed (› Activated when nothing is enabled). */
function FlowStrip({ pipeline }: { pipeline: EvePipelinePhase[] }) {
  const enabled = pipeline.filter((p) => p.enabled === true);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <BadgeStatus status="pending-activation" size="sm" showDescription={false} />
      {enabled.map((phase) => (
        <span key={phase.key} className="flex items-center gap-1.5">
          <ChevronRightIcon className="size-3 text-muted-foreground" />
          <BadgeStatus status={entryStatusFor(phase)} pipeline={pipeline} size="sm" showDescription={false} />
        </span>
      ))}
      <ChevronRightIcon className="size-3 text-muted-foreground" />
      <BadgeStatus status={enabled.length > 0 ? "completed" : "activated"} size="sm" showDescription={false} />
    </div>
  );
}

/**
 * Pipeline overview: pause/resume Amy's automation (instant save), the
 * campaign-level amy_instructions (dirty-state save), and the creator flow.
 * Writes ride the amy action routes — never Flask (AGENTS.md: dashboard code
 * must not $set eve_* fields). Everything here but the flow is campaign-level
 * whatever the tier. Multi-tier campaigns (`tiers`) get the "Different per
 * tier" switch in the flow's header and, in the per-tier view, one flow row
 * per tier; otherwise the flow shows `pipeline` (the shared one).
 */
export function OverviewSection({
  pipeline,
  tiers,
}: {
  pipeline: EvePipelinePhase[];
  /** Multi-tier campaigns only. */
  tiers?: {
    count: number;
    perTier: boolean;
    onPerTierChange: (on: boolean) => void;
    /** The per-tier view's rows, in tier order. */
    flows: TierFlow[];
  };
}) {
  const { campaign, campaignId } = useCampaignStore();
  const { getToken, orgId } = useAuth();
  const { setCampaignAutopilot, setAmyInstructions, isSavingConfig } = useEveActionsStore();

  const serverInstructions = campaign?.amy_instructions ?? "";
  const [instructions, setInstructions] = useState(serverInstructions);
  useEffect(() => {
    setInstructions(serverInstructions);
  }, [serverInstructions]);
  const instructionsDirty = instructions !== serverInstructions;

  const running = campaign?.eve_autopilot === true;
  const enabledPhases = pipeline.filter((p) => p.enabled === true);

  const withAuth = async (fn: (orgIdValue: string, token: string) => Promise<unknown>) => {
    const token = await getToken();
    if (!token || !campaignId || !orgId) return console.error("No token found");
    await fn(orgId, token);
  };

  const handleAutopilot = (value: boolean) =>
    withAuth((orgIdValue, token) =>
      setCampaignAutopilot(campaignId!, value, orgIdValue, token)
    );

  const handleSaveInstructions = () =>
    withAuth((orgIdValue, token) =>
      setAmyInstructions(campaignId!, instructions, orgIdValue, token)
    );

  return (
    <div className="space-y-6">
      <Frame>
        <FrameHeader>
          <FrameTitle className="flex items-center gap-1 mb-1">
            Run by
            <AmyBadge className="text-xs font-medium py-0.5 pl-0.5 bg-background" imgClassName="size-5" />
          </FrameTitle>
        </FrameHeader>
        <SwitchCard disabled={isSavingConfig}>
          <SwitchCardSwitch
            id="eve_autopilot"
            checked={running}
            onCheckedChange={handleAutopilot}
          />
          <SwitchCardContent>
            <SwitchCardLabel htmlFor="eve_autopilot">
              {!isSavingConfig && running && <PlayCircleIcon />}
              {!isSavingConfig && !running && <PauseCircleIcon />}
              {isSavingConfig && <Spinner />}
              Amy automation
              <span className="text-muted-foreground text-xs">
                {" "}
                - {running ? "running" : "paused"}
              </span>
            </SwitchCardLabel>
          </SwitchCardContent>
        </SwitchCard>
      </Frame>

      <Frame>
        <FrameHeader>
          <FrameTitle>Instructions for Amy</FrameTitle>
        </FrameHeader>
        {/* Panel-styled textarea (StagesTab template idiom) — a Textarea
            inside a FramePanel would be a box inside a box. */}
        <Textarea
          variant="panel"
          className="min-h-32"
          placeholder={
            'e.g. "Never go above $500 without asking. On waiting-approval, append the creator to the tracking Sheet."'
          }
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          disabled={isSavingConfig}
        />
        {instructionsDirty && (
          <FrameFooter className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="secondary"
              className="animate-in fade-in-0 slide-in-from-right-4 duration-200"
              disabled={isSavingConfig}
              onClick={() => setInstructions(serverInstructions)}
              aria-label="Discard instruction changes"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="animate-in fade-in-0 slide-in-from-right-2 duration-200"
              disabled={isSavingConfig}
              onClick={handleSaveInstructions}
            >
              Save Instructions
            </Button>
          </FrameFooter>
        )}
      </Frame>

      {(tiers || enabledPhases.length > 0) && (
        <Frame>
          <FrameHeader>
            <FrameTitle>Creator flow</FrameTitle>
            {tiers && (
              <FrameAction>
                <Label htmlFor="per_tier_pipelines" className="text-xs font-normal text-muted-foreground">
                  Different per tier
                </Label>
                <Switch
                  id="per_tier_pipelines"
                  size="sm"
                  checked={tiers.perTier}
                  onCheckedChange={tiers.onPerTierChange}
                  disabled={isSavingConfig}
                />
              </FrameAction>
            )}
          </FrameHeader>
          {tiers?.perTier ? (
            <FramePanel className="divide-y p-0">
              {tiers.flows.map((flow) => (
                <div key={flow.tier} data-context-menu className="flex items-start gap-3 px-4 py-3 sm:items-center sm:gap-4">
                  <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                    <div className="flex min-w-0 flex-col sm:w-36 sm:shrink-0">
                      <span className="text-sm font-medium">Tier {flow.tier}</span>
                      {flow.offer && <span className="truncate text-xs text-muted-foreground">{flow.offer}</span>}
                    </div>
                    <FlowStrip pipeline={flow.pipeline} />
                  </div>
                  {flow.menu}
                </div>
              ))}
            </FramePanel>
          ) : (
            <FramePanel className="p-3">
              <FlowStrip pipeline={pipeline} />
            </FramePanel>
          )}
        </Frame>
      )}
    </div>
  );
}
